import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AddressWidget from "../../../components/Address/AddressWidget";
import HeaderBar from "../../../components/Header/HeaderBar";
import ImageNoteScroller from "../../../components/Images/ImageNoteScroller";
import ImageWithNotesScroller from "../../../components/Images/ImageWithNotesScroller";
import LabeledTextboxInput from "../../../components/Inputs/LabeledTextboxInput";
import LoadingIcon from "../../../components/Loaders/LoadingIcon";
import ServicesField from "../../../components/Services/ServicesField";
import MaterialsConsumedTable from "../../../components/Tables/MaterialsConsumedTable";
import CrossCircleRed from "../../../resources/cross-circle-red.svg";
import backend from "../../../services/axios/backendAxios";
import axiosJobHistory from "../../../services/axios/jobHistory";
import flash from "../../../services/flash";
import axios from "../../../services/axios/backendAxios";
import errors from "../../../services/errors";
import InputWrapper from "../../../components/Inputs/InputWrapper";
import SubtleButton from "../../../components/Buttons/SubtleButton";
import { format, formatDistanceStrict } from "date-fns";
import LabeledField from "../../../components/Labels/LabeledField";
import { distance } from "@turf/turf";
import { JobHistory } from "../../../types";
import { useQueryClient } from "@tanstack/react-query";
import useDownloadHelper from "../../../hooks/useDownloadHelper";
import { useSelector } from "react-redux";
import { ReduxState } from "../../../store/storeTypes";
import { auth, relativeServicedByInfo } from "@mysnowscape/snow-lib";
const AdminJobHistoryDetailsPage = () => {
  const { jobHistoryId } = useParams();

  const [loading, setLoading] = useState(true);
  const [jobHistory, setJobHistory] = useState<Partial<JobHistory>>({});
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const downloadPDF = useDownloadHelper("/job/history/pdf");
  const currentCompanyId = useSelector(
    (state: ReduxState) => state.currentUser.currentCompanyId,
  );
  const currentUser = useSelector((state: ReduxState) => state.currentUser);

  useEffect(() => {
    setLoading(true);
    axios
      .get("job/history/" + jobHistoryId)
      .then((res) => {
        setJobHistory(res.data);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [jobHistoryId]);

  const handleResendReportEmail = () => {
    if (!jobHistoryId) return;

    axiosJobHistory.resendReportEmail(
      { jobHistoryId },
      () => {
        alert("Successfully Sent");
      },
      (err) => {
        alert(err);
      },
    );
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const updateMaterials = (key: string, val: any) => {
    const b = { ...jobHistory };
    // check if valid number
    if (!isNaN(Number(val))) {
      b.materialsConsumed[key].quantity = Number(val);
      setJobHistory(b);
      backend
        .patch(`/material-consumed/${b.materialsConsumed[key]._id}`, {
          quantity: val,
        })
        .then(() => {
          flash.success("Saved: Material consumption updated");
        });
    }
  };

  const updateJobHistory = useCallback(
    async (jobHistory: Partial<JobHistory>) => {
      try {
        const data = (
          await axios.post(
            `job/history/add?currentCompanyId=${currentCompanyId}&return_with_materials=1`,
            jobHistory,
          )
        ).data;
        setJobHistory(data.jobHistory);
        flash.success("Property history updated.");
        setIsDirty(false);
      } catch (err) {
        errors.report(err);
        flash.error(
          "Unable to save your changes.  Please refresh and try again.",
        );
      } finally {
        setSaving(false);
      }
    },
    [currentCompanyId],
  );

  const handleClearFlaggedForReviewClick = () => {
    updateJobHistory({
      ...jobHistory,
      flaggedForReview: false,
    });
  };

  const modifyJH = useCallback(
    (newJobHistoryData: Partial<JobHistory>) => {
      setJobHistory(newJobHistoryData);
      setIsDirty(true);
    },
    [setJobHistory, setIsDirty],
  );

  // Marking an in-progress job (no end time) as finished. We warn only when
  // the job started less than a week ago, since that's when unsynced phone
  // data may still be in flight. Older records finish without a prompt.
  const handleMarkFinished = () => {
    const startedLessThanAWeekAgo = jobHistory.startTimeUTC
      ? Date.now() - new Date(jobHistory.startTimeUTC).getTime() <
        7 * 24 * 60 * 60 * 1000
      : false;
    if (
      startedLessThanAWeekAgo &&
      !confirm(
        "Marking this job as finished sets its end time and sends the customer a completion report when you save.\n\n" +
          "Any work not yet synced from the operator's phone will be lost — if possible, have them sync from the app first.\n\n" +
          "Mark this job as finished?",
      )
    ) {
      return;
    }
    modifyJH({ ...jobHistory, endTimeUTC: new Date().toISOString() });
  };

  const buttons: Array<{
    color?: string;
    label?: string;
    disabled?: boolean;
    onClick?: () => void;
    loading?: boolean;
  }> = [
    {
      color: "blue",
      label: "Resend Report",
      disabled: isEditing,
      onClick: () => handleResendReportEmail(),
    },
    {
      // PDF renders the saved record server-side, so disable it while editing
      // (rather than acting on unsaved changes) — mirrors Resend Report.
      color: "blue",
      label: "PDF",
      disabled: isEditing,
      onClick: () => downloadPDF({ jobHistoryId: jobHistoryId }),
    },
  ];
  if (isEditing) {
    buttons.push({
      color: "red",
      // disabled: !isDirty,
      label: "Delete",
      onClick: async () => {
        if (confirm("Delete Property History?")) {
          await axios.delete(`job/history/${jobHistoryId}/delete`);
          flash.success("Deleted Property Report");
          queryClient.invalidateQueries({
            queryKey: ["jobs"],
          });
          navigate("/admin/property/history");
        }
      },
    });
    buttons.push({
      color: "green",
      // disabled: !isDirty,
      label: "Save Changes",
      loading: saving,
      onClick: () => {
        updateJobHistory(jobHistory);
        setIsEditing(false);
      },
    });
  } else {
    buttons.push({
      color: "blue",
      label: "Edit Record",
      onClick: () => setIsEditing(true),
      disabled: !(
        jobHistory?.companyId &&
        currentUser &&
        auth.canAdminCompany(currentUser, jobHistory?.companyId)
      ),
    });
  }

  const metersFromSite =
    jobHistory?.location?.lng && jobHistory?.crewLocation?.lng
      ? Math.round(
          distance(
            [jobHistory.location.lng, jobHistory.location.lat],
            [jobHistory.crewLocation.lng, jobHistory.crewLocation.lat],
            { units: "meters" },
          ),
        )
      : null;
  return (
    <div className="flex flex-col items-center">
      <HeaderBar title="Property Service Record" buttons={buttons} />
      {loading ? (
        <LoadingIcon />
      ) : (
        <div
          className={`flex flex-1 flex-col w-full p-10 items-center overflow-y-scroll mb-8`}
        >
          {jobHistory.flaggedForReview && (
            <div className="flex items-center justify-between px-8 bg-snow-red w-full mb-8 py-4 rounded-lg">
              <span className="text-xl text-full-white">
                Flagged for Review
              </span>
              <div
                className="text-full-white cursor-pointer"
                onClick={handleClearFlaggedForReviewClick}
              >
                <img
                  className="w-12"
                  src={CrossCircleRed}
                  alt="Unflag for review"
                />
              </div>
            </div>
          )}
          <div className="flex flex-col w-full justify-between sm:flex-row">
            <div className="text-3xl font-medium text-snow-primary">
              {jobHistory.name}
            </div>
            <AddressWidget address={jobHistory.address} />
          </div>
          <p className="text-sm text-snow-primary w-full mb-0">
            Serviced by{" "}
            {currentCompanyId
              ? relativeServicedByInfo(
                  jobHistory as JobHistory,
                  currentCompanyId,
                )
              : ""}
          </p>
          {isEditing ? (
            <>
              <InputWrapper
                label="Start Time"
                description="Select the start time for this service record"
                className="mb-0"
              >
                <input
                  className="w-full text-lg font-regular border-none bg-transparent"
                  onChange={(e) =>
                    modifyJH({
                      ...jobHistory,
                      startTimeUTC: new Date(e.target.value).toISOString(),
                    })
                  }
                  type="datetime-local"
                  value={
                    jobHistory.startTimeUTC
                      ? format(
                          new Date(jobHistory.startTimeUTC),
                          "yyyy-MM-dd'T'HH:mm",
                        )
                      : undefined
                  }
                  max={
                    jobHistory.endTimeUTC
                      ? format(
                          new Date(jobHistory.endTimeUTC),
                          "yyyy-MM-dd'T'HH:mm",
                        )
                      : undefined
                  }
                />
              </InputWrapper>
              <InputWrapper
                label="End Time"
                description={
                  jobHistory.endTimeUTC
                    ? "Select the end time for this service record"
                    : "This job is still in progress. Mark it finished to set an end time, then Save Changes."
                }
              >
                {jobHistory.endTimeUTC ? (
                  <input
                    className="w-full text-lg font-regular border-none bg-transparent"
                    onChange={(e) =>
                      modifyJH({
                        ...jobHistory,
                        endTimeUTC: new Date(e.target.value).toISOString(),
                      })
                    }
                    type="datetime-local"
                    value={format(
                      new Date(jobHistory.endTimeUTC),
                      "yyyy-MM-dd'T'HH:mm",
                    )}
                    min={
                      jobHistory.startTimeUTC
                        ? format(
                            new Date(jobHistory.startTimeUTC),
                            "yyyy-MM-dd'T'HH:mm",
                          )
                        : undefined
                    }
                  />
                ) : (
                  <SubtleButton
                    onClick={handleMarkFinished}
                    label="Mark as Finished"
                  />
                )}
              </InputWrapper>
            </>
          ) : (
            <p className="text-sm text-snow-primary w-full">
              At {formatTime(jobHistory.startTimeUTC)} until{" "}
              {formatTime(jobHistory.endTimeUTC)}{" "}
              {jobHistory.endTimeUTC
                ? "(" +
                  formatDistanceStrict(
                    new Date(jobHistory.endTimeUTC),
                    new Date(jobHistory.startTimeUTC!),
                  ) +
                  ")"
                : ""}
            </p>
          )}
          {/* Only show dates for Job Histories recorded after we started recording dates: */}
          {jobHistory.endTimeUTC &&
            new Date(jobHistory.endTimeUTC) >
              new Date("2025-10-28T00:00:00.000Z") && (
              <p className="text-xs text-snow-primary w-full mb-4">
                {metersFromSite ? (
                  <>
                    {metersFromSite < 30
                      ? "Crew was on location when they submitted this report."
                      : `Crew was ${metersFromSite} meters from the property center when they submitted this report.`}
                  </>
                ) : (
                  <>Crew location unknown (GPS off?)</>
                )}
              </p>
            )}

          <ImageNoteScroller
            enableUpload={false}
            imageArray={jobHistory.sitemaps}
            isSitemap={true}
            label="Sitemaps"
          />
          <LabeledField label="Instructions">
            <p>{jobHistory.instructions}</p>
          </LabeledField>
          {isEditing ? (
            <LabeledTextboxInput
              disabled={false}
              label="Operator Notes"
              multiline={true}
              value={jobHistory.notes}
              setValue={(value: string) => {
                modifyJH({ ...jobHistory, notes: value });
              }}
            />
          ) : (
            <LabeledField label="Operator Notes">
              <p>{jobHistory.notes}</p>
            </LabeledField>
          )}
          <ServicesField
            key="service-fields"
            label="Services Completed"
            subtitle="The following services were completed during this visit to the property."
            services={jobHistory.servicesPerformed}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            setServices={(newServices: any) => {
              modifyJH({ ...jobHistory, servicesPerformed: newServices });
            }}
            showSubOptions={true}
            readOnly={!isEditing}
            hideUnchecked={!isEditing}
          />
          <MaterialsConsumedTable
            materialsConsumed={jobHistory.materialsConsumed}
            onMaterialUpdate={updateMaterials}
            readOnly={!isEditing}
          ></MaterialsConsumedTable>
          <LabeledField
            label={`Property Site Before Images (${jobHistory.beforeImages?.length ?? 0})`}
          >
            <ImageWithNotesScroller
              enableUpload={isEditing}
              readOnly={!isEditing}
              imageArray={jobHistory.beforeImages}
              label="Property Site Before Images"
              enableDelete={isEditing}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              setImageArray={(newArray: any) => {
                modifyJH({ ...jobHistory, beforeImages: newArray });
              }}
            />
          </LabeledField>
          <LabeledField
            label={`Property Site After Images (${jobHistory.completedJobImages?.length ?? 0})`}
          >
            <ImageWithNotesScroller
              enableUpload={isEditing}
              readOnly={!isEditing}
              imageArray={jobHistory.completedJobImages}
              label="Property Site After Images"
              enableDelete={isEditing}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              setImageArray={(newArray: any) => {
                modifyJH({ ...jobHistory, completedJobImages: newArray });
              }}
            />
          </LabeledField>
          <LabeledField label="Weather During Service">
            <p>{jobHistory.weatherCondition ?? "Unknown"}</p>
          </LabeledField>
          {jobHistory.updatedBy ? (
            <div className="py-4">
              <span style={{ marginRight: 7 }}>&#9432;</span>Notice: This record
              was updated at{" "}
              {format(new Date(jobHistory.updatedAt), "yyyy-MM-dd HH:mm")} by{" "}
              {jobHistory.updatedBy?.firstName} {jobHistory.updatedBy?.lastName}
              .
            </div>
          ) : null}
          <div className="text-snow-light-grey text-xs">
            Record ID: {jobHistory._id}
          </div>
        </div>
      )}
    </div>
  );
};
function formatTime(dateString: string | null | undefined) {
  try {
    if (dateString) {
      const date = new Date(dateString);
      return format(date, "MMM d, yyyy h:mm bbb");
    } else {
      return "In Progress";
    }
  } catch (err) {
    errors.report(err, { message: "Unable to format time on a job history" });
  }
}
export default AdminJobHistoryDetailsPage;
